import { useState, useEffect } from 'react';
import { useOutletContext, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Calendar, Plus, Search, Filter, Clock, MapPin, Trash2, Edit2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ClubSessions = () => {
  const { club } = useOutletContext();
  const { clubId } = useParams();
  const [sessions, setSessions] = useState([]);
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterTeam, setFilterTeam] = useState('all');
  const [filterType, setFilterType] = useState('all');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [formData, setFormData] = useState({
    team_id: '',
    title: '',
    session_type: 'training',
    date: '',
    duration_minutes: 90,
    location: '',
    description: ''
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    const token = localStorage.getItem('club_token');
    try {
      const [teamsRes] = await Promise.all([
        axios.get(`${API}/clubs/teams`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: [] }))
      ]);
      setTeams(teamsRes.data || []);

      // Fetch sessions for all teams
      const allSessions = [];
      for (const team of (teamsRes.data || [])) {
        try {
          const sessionsRes = await axios.get(`${API}/teams/${team.id}/sessions`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          allSessions.push(...(sessionsRes.data || []).map(s => ({ ...s, team_name: team.name })));
        } catch (e) {
          // Team might not have sessions endpoint yet
        }
      }
      setSessions(allSessions);
    } catch (error) {
      console.error('Failed to fetch data:', error);
    }
    setLoading(false);
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!formData.team_id) {
      toast.error('Please select a team');
      return;
    }

    const token = localStorage.getItem('club_token');
    try {
      await axios.post(`${API}/teams/${formData.team_id}/sessions`, {
        title: formData.title,
        session_type: formData.session_type,
        date: formData.date,
        duration_minutes: formData.duration_minutes,
        location: formData.location,
        description: formData.description
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Session created!');
      setShowCreateModal(false);
      setFormData({
        team_id: '',
        title: '',
        session_type: 'training',
        date: '',
        duration_minutes: 90,
        location: '',
        description: ''
      });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create session');
    }
  };

  const handleDelete = async (teamId, sessionId) => {
    if (!confirm('Are you sure you want to cancel this session?')) return;
    const token = localStorage.getItem('club_token');
    try {
      await axios.delete(`${API}/teams/${teamId}/sessions/${sessionId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Session cancelled');
      fetchData();
    } catch (error) {
      toast.error('Failed to cancel session');
    }
  };

  const getTeamName = (teamId) => {
    const team = teams.find(t => t.id === teamId);
    return team?.name || 'Unknown';
  };

  const filteredSessions = sessions.filter(session => {
    const matchesSearch = session.title?.toLowerCase().includes(search.toLowerCase());
    const matchesTeam = filterTeam === 'all' || session.team_id === filterTeam;
    const matchesType = filterType === 'all' || session.session_type === filterType;
    return matchesSearch && matchesTeam && matchesType;
  }).sort((a, b) => new Date(a.date) - new Date(b.date));

  const sessionTypeColors = {
    training: 'bg-emerald-500/20 text-emerald-400',
    game: 'bg-blue-500/20 text-blue-400',
    tournament: 'bg-purple-500/20 text-purple-400',
    meeting: 'bg-yellow-500/20 text-yellow-400'
  };

  return (
    <div className="space-y-6" data-testid="club-sessions">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl md:text-3xl uppercase">
            Sessions & <span className="text-emerald-400">Events</span>
          </h1>
          <p className="text-muted-foreground text-sm mt-1">
            Manage team sessions, matches, and tournaments
          </p>
        </div>
        <Button 
          onClick={() => setShowCreateModal(true)}
          className="bg-emerald-500 text-black hover:bg-emerald-400"
          disabled={teams.length === 0}
        >
          <Plus size={18} className="mr-2" />
          Create Session
        </Button>
      </div>

      {teams.length === 0 && (
        <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-sm p-4">
          <p className="text-sm text-yellow-400">
            Create a team first before scheduling sessions.
          </p>
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" size={18} />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search sessions..."
            className="bg-transparent border-border pl-10"
          />
        </div>
        <div className="flex gap-2">
          <select
            value={filterTeam}
            onChange={(e) => setFilterTeam(e.target.value)}
            className="bg-transparent border border-border rounded-sm p-2 text-sm text-white"
          >
            <option value="all" className="bg-card">All Teams</option>
            {teams.map(team => (
              <option key={team.id} value={team.id} className="bg-card">{team.name}</option>
            ))}
          </select>
          <select
            value={filterType}
            onChange={(e) => setFilterType(e.target.value)}
            className="bg-transparent border border-border rounded-sm p-2 text-sm text-white"
          >
            <option value="all" className="bg-card">All Types</option>
            <option value="training" className="bg-card">Training</option>
            <option value="game" className="bg-card">Game</option>
            <option value="tournament" className="bg-card">Tournament</option>
            <option value="meeting" className="bg-card">Meeting</option>
          </select>
        </div>
      </div>

      {/* Sessions List */}
      {loading ? (
        <div className="text-center py-12">
          <div className="animate-spin w-8 h-8 border-2 border-emerald-500 border-t-transparent rounded-full mx-auto"></div>
        </div>
      ) : filteredSessions.length === 0 ? (
        <div className="text-center py-12 bg-card border border-border/40 rounded-sm">
          <Calendar className="mx-auto text-muted-foreground mb-4" size={48} />
          <p className="text-muted-foreground mb-4">
            {search || filterTeam !== 'all' || filterType !== 'all' 
              ? 'No sessions found matching filters' 
              : 'No sessions scheduled yet'}
          </p>
          {teams.length > 0 && !search && filterTeam === 'all' && (
            <Button onClick={() => setShowCreateModal(true)} className="bg-emerald-500 text-black hover:bg-emerald-400">
              <Plus size={18} className="mr-2" />
              Schedule Your First Session
            </Button>
          )}
        </div>
      ) : (
        <div className="space-y-3">
          {filteredSessions.map((session, idx) => (
            <motion.div
              key={session.id}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: idx * 0.03 }}
              className="bg-card border border-border/40 rounded-sm p-4 hover:border-emerald-500/30 transition-colors"
            >
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div className="flex items-start gap-4">
                  <div className="w-14 h-14 bg-emerald-500/10 rounded-sm flex flex-col items-center justify-center flex-shrink-0">
                    <span className="text-xs text-muted-foreground uppercase">
                      {new Date(session.date).toLocaleDateString('en-US', { month: 'short' })}
                    </span>
                    <span className="font-heading text-lg text-emerald-400">
                      {new Date(session.date).getDate()}
                    </span>
                  </div>
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="font-semibold">{session.title}</h3>
                      <span className={`text-xs px-2 py-0.5 rounded-sm ${sessionTypeColors[session.session_type]}`}>
                        {session.session_type}
                      </span>
                    </div>
                    <p className="text-sm text-muted-foreground mb-2">
                      {session.team_name || getTeamName(session.team_id)}
                    </p>
                    <div className="flex flex-wrap gap-4 text-xs text-muted-foreground">
                      <span className="flex items-center gap-1">
                        <Clock size={14} />
                        {new Date(session.date).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}
                        {session.duration_minutes && ` (${session.duration_minutes} min)`}
                      </span>
                      {session.location && (
                        <span className="flex items-center gap-1">
                          <MapPin size={14} />
                          {session.location}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
                <div className="flex gap-2 md:flex-col">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleDelete(session.team_id, session.id)}
                    className="text-muted-foreground hover:text-red-400"
                  >
                    <Trash2 size={16} />
                  </Button>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      )}

      {/* Create Session Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4 overflow-y-auto" onClick={() => setShowCreateModal(false)}>
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-card border border-border/40 rounded-sm w-full max-w-md p-6 my-8"
            onClick={(e) => e.stopPropagation()}
          >
            <h2 className="font-heading text-xl uppercase mb-6">Create Session</h2>
            
            <form onSubmit={handleCreate} className="space-y-4">
              <div>
                <label className="text-sm uppercase tracking-widest text-muted-foreground mb-2 block">
                  Team *
                </label>
                <select
                  value={formData.team_id}
                  onChange={(e) => setFormData({...formData, team_id: e.target.value})}
                  required
                  className="w-full bg-transparent border border-border rounded-sm p-3 text-white"
                >
                  <option value="" className="bg-card">Select a team</option>
                  {teams.filter(t => t.status === 'active').map(team => (
                    <option key={team.id} value={team.id} className="bg-card">{team.name}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="text-sm uppercase tracking-widest text-muted-foreground mb-2 block">
                  Title *
                </label>
                <Input
                  value={formData.title}
                  onChange={(e) => setFormData({...formData, title: e.target.value})}
                  required
                  className="bg-transparent border-border focus:border-emerald-500"
                  placeholder="e.g., U14 Training Session"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm uppercase tracking-widest text-muted-foreground mb-2 block">
                    Type
                  </label>
                  <select
                    value={formData.session_type}
                    onChange={(e) => setFormData({...formData, session_type: e.target.value})}
                    className="w-full bg-transparent border border-border rounded-sm p-3 text-white"
                  >
                    <option value="training" className="bg-card">Training</option>
                    <option value="game" className="bg-card">Game</option>
                    <option value="tournament" className="bg-card">Tournament</option>
                    <option value="meeting" className="bg-card">Meeting</option>
                  </select>
                </div>
                <div>
                  <label className="text-sm uppercase tracking-widest text-muted-foreground mb-2 block">
                    Duration (min)
                  </label>
                  <Input
                    type="number"
                    value={formData.duration_minutes}
                    onChange={(e) => setFormData({...formData, duration_minutes: parseInt(e.target.value)})}
                    min={15}
                    max={480}
                    className="bg-transparent border-border focus:border-emerald-500"
                  />
                </div>
              </div>

              <div>
                <label className="text-sm uppercase tracking-widest text-muted-foreground mb-2 block">
                  Date & Time *
                </label>
                <Input
                  type="datetime-local"
                  value={formData.date}
                  onChange={(e) => setFormData({...formData, date: e.target.value})}
                  required
                  className="bg-transparent border-border focus:border-emerald-500"
                />
              </div>

              <div>
                <label className="text-sm uppercase tracking-widest text-muted-foreground mb-2 block">
                  Location
                </label>
                <Input
                  value={formData.location}
                  onChange={(e) => setFormData({...formData, location: e.target.value})}
                  className="bg-transparent border-border focus:border-emerald-500"
                  placeholder="e.g., Main Field"
                />
              </div>

              <div>
                <label className="text-sm uppercase tracking-widest text-muted-foreground mb-2 block">
                  Description
                </label>
                <Textarea
                  value={formData.description}
                  onChange={(e) => setFormData({...formData, description: e.target.value})}
                  className="bg-transparent border-border focus:border-emerald-500 min-h-[80px]"
                  placeholder="Optional notes..."
                />
              </div>

              <div className="flex gap-3 pt-4">
                <Button 
                  type="button" 
                  variant="outline" 
                  onClick={() => setShowCreateModal(false)}
                  className="flex-1"
                >
                  Cancel
                </Button>
                <Button 
                  type="submit"
                  className="flex-1 bg-emerald-500 text-black hover:bg-emerald-400"
                >
                  Create Session
                </Button>
              </div>
            </form>
          </motion.div>
        </div>
      )}
    </div>
  );
};

export default ClubSessions;
